import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Mail, Smartphone } from "lucide-react";

import { signIn } from "@/lib/session";
import { decodeJwt } from "@/lib/jwt";
import { isSixDigits, maskEmail, maskPhone } from "@/lib/authHelpers";
import { resolveScope, resolveStaffIds } from "@/lib/authScope";
import { getMerchantClientId } from "@/lib/payerClient";
import { createLocalSession } from "@/hooks/useLocalSession";
import { clearTempLogin, setAuth, setLoginContext } from "@/store/slices/authSlice";
import { useSendOtpMutation, useVerifyOtpMutation } from "@/features/auth/api/authApi";
import type { RootState } from "@/store/store";
import { safeStorage } from "@/shared/lib/safeStorage";

import { getRedirectHref } from "../lib/redirect";
import { AUTH_STORAGE_KEYS } from "../lib/storageKeys";
import { OtpInput } from "./OtpInput";

import { Alert, AlertDescription } from "@/shared/ui/alert/Alert";
import { Button } from "@/shared/ui/button/Button";
import { cn } from "@/shared/lib/cn";

import { AuthCard, AuthShell } from "./AuthShell";

type OtpMethod = "EMAIL" | "SMS";

const COOLDOWN_SECONDS = 30;

interface TokenClaims {
  role?: string;
  staffType?: string;
  staffTypeId?: number;
  source?: string;
  isAgent?: boolean;
  merchantId?: string;
  targetDatabase?: string;
  clientId?: number;
  clinicId?: number;
  employerId?: number;
  groupId?: number;
  divisionId?: number;
  locationId?: number;
  memberId?: number;
}

interface MethodButtonProps {
  method: OtpMethod;
  masked: string;
  fallbackLabel: string;
  active: boolean;
  sending: boolean;
  cooldown: number;
  disabled: boolean;
  onSend: (method: OtpMethod) => void;
}

function MethodButton({
  method,
  masked,
  fallbackLabel,
  active,
  sending,
  cooldown,
  disabled,
  onSend,
}: MethodButtonProps) {
  const Icon = method === "EMAIL" ? Mail : Smartphone;
  const label = method === "EMAIL" ? "Email" : "SMS";
  const trailing = sending
    ? "Sending…"
    : cooldown > 0 && active
      ? `Resend in ${cooldown}s`
      : "Send OTP";

  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => onSend(method)}
      disabled={disabled}
      className={cn(
        "h-auto w-full justify-between px-4 py-3",
        active && "border-primary bg-accent"
      )}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border bg-muted">
          <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
        </span>
        <span className="min-w-0 text-left">
          <span className="block text-sm font-medium text-foreground">{label}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {masked || fallbackLabel}
          </span>
        </span>
      </span>
      {/* The countdown must never run past the card's edge: it keeps its width and the address
          beside it truncates instead. */}
      <span className="shrink-0 whitespace-nowrap pl-2 text-sm text-primary">{trailing}</span>
    </Button>
  );
}

/**
 * Two-step OTP verification. Ports `src/app/verify-otp/page.tsx`: send a code
 * over EMAIL or SMS (per-channel 30s resend cooldown), verify the 6-digit
 * code, then establish the session via `signIn("credentials", …)` and route
 * to the scope's landing page. Send/verify API calls are unchanged.
 */
export function VerifyOtpView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const temp = useSelector((s: RootState) => s.auth.tempLogin);
  const user = useSelector((s: RootState) => s.auth.user);

  const [activeMethod, setActiveMethod] = useState<OtpMethod | null>(null);
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [sendingMethod, setSendingMethod] = useState<OtpMethod | null>(null);
  const [cooldownEmail, setCooldownEmail] = useState(0);
  const [cooldownSms, setCooldownSms] = useState(0);

  const [sendOtp] = useSendOtpMutation();
  const [verifyOtp, { isLoading: verifying }] = useVerifyOtpMutation();

  const maskedEmail = maskEmail(temp?.emailId);
  const maskedPhone = maskPhone(temp?.phoneNumber);

  // No pending login and no established user → bounce to login.
  useEffect(() => {
    if (!temp && !user) navigate("/login", { replace: true });
  }, [temp, user, navigate]);

  useEffect(() => {
    if (cooldownEmail <= 0) return;
    const t = setInterval(() => setCooldownEmail((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldownEmail]);

  useEffect(() => {
    if (cooldownSms <= 0) return;
    const t = setInterval(() => setCooldownSms((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldownSms]);

  // One line of status lives in the card subtitle — no separate banners.
  const subtitle = !activeMethod
    ? "Choose where to receive your one-time code."
    : `Enter the code we sent to ${activeMethod === "EMAIL" ? maskedEmail : maskedPhone}.`;

  const focusFirstBox = () => {
    setTimeout(() => {
      const firstBox = document.querySelector<HTMLInputElement>("input[data-otp-box='true']");
      firstBox?.focus();
    }, 0);
  };

  const handleSend = useCallback(
    async (method: OtpMethod) => {
      if (!temp) return;
      setErr(null);
      setSendingMethod(method);
      try {
        await sendOtp({
          userName: temp.username,
          merchantId: temp.merchantId,
          otpChannel: method,
        }).unwrap();

        setActiveMethod(method);
        if (method === "EMAIL") setCooldownEmail(COOLDOWN_SECONDS);
        else setCooldownSms(COOLDOWN_SECONDS);
        focusFirstBox();
      } catch (e) {
        setErr(
          (e as { data?: { message?: string } })?.data?.message ??
            "Failed to send OTP. Please try again."
        );
      } finally {
        setSendingMethod(null);
      }
    },
    [temp, sendOtp]
  );

  const doVerify = useCallback(
    async (codeRaw?: string) => {
      if (!temp || !activeMethod) return;
      setErr(null);
      const code = (codeRaw ?? otp).replace(/\D/g, "");
      if (!isSixDigits(code)) {
        setErr("Please enter the 6-digit OTP.");
        return;
      }
      try {
        const res = await verifyOtp({
          userName: temp.username,
          merchantId: temp.merchantId,
          otp: code,
          otpChannel: activeMethod,
        }).unwrap();

        const loginToken = res?.jwtToken;
        if (!loginToken) {
          setErr("Invalid server response.");
          return;
        }

        const signed = await signIn("credentials", {
          redirect: false,
          loginToken,
          merchantId: temp.merchantId,
        });
        if (signed?.error) {
          setErr("Unable to start session.");
          return;
        }

        const local = createLocalSession(temp.username);
        const p = (decodeJwt(loginToken) as TokenClaims | null) ?? {};
        // Dev-parity scope resolution: staffType drives the scope (CLIENT →
        // payer, PROVIDER/ADMIN → clinic, …); the PGLX platform source is the
        // super-admin fallback when no staff seat is present.
        const normalizedSource = resolveScope({
          staffType: p.staffType,
          source: p.source,
          targetDatabase: p.targetDatabase,
          role: p.role,
        });
        const merchantIdFromToken = p.merchantId ?? p.targetDatabase ?? temp.merchantId;

        dispatch(
          setAuth({
            user: {
              username: temp.username,
              role: p.role?.toLowerCase(),
              merchantId: merchantIdFromToken,
              source: normalizedSource,
              isAgent: p.isAgent === true,
              ...resolveStaffIds(p),
            },
            sessionId: local.sessionId,
          })
        );

        if (normalizedSource === "payer" && merchantIdFromToken) {
          const clientId = getMerchantClientId(merchantIdFromToken);
          if (clientId) {
            safeStorage.setItem(AUTH_STORAGE_KEYS.PAYER_MERCHANT_ID, merchantIdFromToken);
          }
        }

        // The login response carries the same context `/auth/me` returns, so
        // the first authenticated render already knows the menu, the full seat
        // chain and whether the password must be changed.
        const context = res.context;
        if (context) {
          dispatch(
            setLoginContext({
              userId: context.userId,
              username: context.username,
              requirePasswordChange: context.requirePasswordChange,
              features: context.features,
              writableFeatures: context.writableFeatures,
              visibleMenuIds: context.visibleMenuIds,
              manageableLevels: context.manageableLevels,
              assignableUserLevels: context.assignableUserLevels,
              commissionSections: context.commissionSections,
              agentMappings: context.agentMappings,
              isAgent: context.isAgent,
              scope: context.scope,
            })
          );
        }

        dispatch(clearTempLogin());
        // An admin typed this account's password; it has to be replaced before
        // anything else, and the server refuses user administration until then.
        if (context?.requirePasswordChange) {
          navigate("/reset-password", { replace: true });
          return;
        }
        // Agents and agency admins land in their portal, the only place that is theirs.
        const agent = context?.isAgent === true || p.isAgent === true;
        navigate(agent ? "/agent-portal" : getRedirectHref(normalizedSource), { replace: true });
      } catch (e) {
        setErr(
          (e as { data?: { message?: string } })?.data?.message ?? "Invalid OTP. Please try again."
        );
      }
    },
    [temp, activeMethod, otp, verifyOtp, dispatch, navigate]
  );

  const handleBack = useCallback(() => {
    dispatch(clearTempLogin());
    navigate("/login", { replace: true });
  }, [dispatch, navigate]);

  if (!temp && !user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <p className="text-sm text-muted-foreground">Redirecting…</p>
      </div>
    );
  }

  const canResendEmail = cooldownEmail === 0;
  const canResendSms = cooldownSms === 0;

  return (
    <AuthShell>
      <AuthCard title="Two-Step Verification" subtitle={subtitle}>
        <div className="space-y-5">
          <div className="space-y-3">
            <MethodButton
              method="EMAIL"
              masked={maskedEmail}
              fallbackLabel="your email"
              active={activeMethod === "EMAIL"}
              sending={sendingMethod === "EMAIL"}
              cooldown={cooldownEmail}
              disabled={sendingMethod === "EMAIL" || (!canResendEmail && activeMethod === "EMAIL")}
              onSend={handleSend}
            />
            <MethodButton
              method="SMS"
              masked={maskedPhone}
              fallbackLabel="your number"
              active={activeMethod === "SMS"}
              sending={sendingMethod === "SMS"}
              cooldown={cooldownSms}
              disabled={sendingMethod === "SMS" || (!canResendSms && activeMethod === "SMS")}
              onSend={handleSend}
            />
          </div>

          {activeMethod && (
            <div className="space-y-3 rounded-lg border bg-card p-4">
              <div className="text-sm font-medium text-foreground">Enter 6-digit code</div>

              <OtpInput
                value={otp}
                onChange={setOtp}
                disabled={verifying}
                onComplete={(code) => {
                  setOtp(code);
                  if (!verifying) void doVerify(code);
                }}
              />

              {err && (
                <Alert variant="destructive">
                  <AlertDescription>{err}</AlertDescription>
                </Alert>
              )}

              <Button
                type="button"
                className="w-full"
                onClick={() => void doVerify()}
                isLoading={verifying}
                disabled={verifying || !isSixDigits(otp)}
              >
                {verifying ? "Verifying…" : "Verify & Continue"}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                Entered the wrong contact?{" "}
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 align-baseline"
                  onClick={handleBack}
                >
                  Use a different email/number
                </Button>
              </p>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={handleBack}
            >
              ← Back to login
            </Button>
            <span>Need help? Contact support</span>
          </div>
        </div>
      </AuthCard>
    </AuthShell>
  );
}
